/**
 * Full-popup Specifikovat visibility — parity KMP `ObjectPopupActions.resolveSpecGates`.
 * Linear chrome only; milestone-mode overlays stay in UI layers.
 *
 * Match by Czech phase **name** (not PHASE_CODE) so milestone-only names like
 * Zřízené VR work the same as Android `PhaseNames.isPhaseNamed`.
 */
import { PHASE_NAME, getPhaseName } from '../constants/object-workflow/phase-codes';

export interface PopupSpecGates {
  showSpecHp: boolean;
  /** Next includes Zřízené VR only (Fotky / VR photo branch). */
  showSpecHpVrBranch: boolean;
  /** Next includes Připravená bábovka. */
  showSpecHpBabovka: boolean;
  showSpecZafuk: boolean;
  showSpecRozvadecPrimaryZafuk: boolean;
  /** Always false — dedicated KONCOVYBOD záfuk spec is gone. Kept for gate shape parity. */
  showSpecKoncovyBodZafuk: boolean;
  showSpecSpojkoviste: boolean;
}

export function popupSpecGatesShowSpec(gates: PopupSpecGates): boolean {
  return (
    gates.showSpecHp ||
    gates.showSpecZafuk ||
    gates.showSpecRozvadecPrimaryZafuk ||
    gates.showSpecKoncovyBodZafuk ||
    gates.showSpecSpojkoviste
  );
}

export function popupSpecGatesZafukMode(gates: PopupSpecGates): boolean {
  return gates.showSpecZafuk || gates.showSpecRozvadecPrimaryZafuk || gates.showSpecKoncovyBodZafuk;
}

function phaseHasName(phaseCode: string | null | undefined, expectedName: string): boolean {
  const code = String(phaseCode ?? '').trim();
  if (!code) return false;
  const name = getPhaseName(code);
  if (name.localeCompare(expectedName, 'cs', { sensitivity: 'accent' }) === 0) return true;
  return name.toLowerCase() === expectedName.toLowerCase();
}

export function resolvePopupSpecGates(opts: {
  objectType: string | null | undefined;
  status: string | null | undefined;
  /** Editable / next linear phase codes (call-site chooses the right list). */
  nextPhaseCodes: readonly string[];
}): PopupSpecGates {
  const typeNorm = String(opts.objectType ?? '').trim().toUpperCase();
  const status = String(opts.status ?? '').trim();
  const next = opts.nextPhaseCodes ?? [];
  const anyNextNamed = (name: string): boolean => next.some((c) => phaseHasName(c, name));

  const showSpecHpVrBranch =
    typeNorm === 'BUDOVA' && anyNextNamed(PHASE_NAME.BUDOVA_ZRIZENE_VR);
  const showSpecHpBabovka =
    typeNorm === 'BUDOVA' && anyNextNamed(PHASE_NAME.BUDOVA_POSTAVENA_BABOVKA);
  const showSpecHp = showSpecHpVrBranch || showSpecHpBabovka;
  const showSpecZafuk =
    typeNorm === 'BUDOVA' &&
    (phaseHasName(status, PHASE_NAME.BUDOVA_ZRIZENE_VR) ||
      phaseHasName(status, PHASE_NAME.BUDOVA_POSTAVENA_BABOVKA) ||
      phaseHasName(status, PHASE_NAME.BUDOVA_PRIPOJENE_PLASTY)) &&
    anyNextNamed(PHASE_NAME.BUDOVA_ZAFOUKNUTO);
  const showSpecRozvadecPrimaryZafuk =
    typeNorm === 'ROZVADEC' && anyNextNamed(PHASE_NAME.ROZVADEC_ZAFOUKNUTO_SLOUPEK);
  const showSpecKoncovyBodZafuk = false;
  // Napojené MT stays editable after Připojené (shared junction).
  const showSpecSpojkoviste = typeNorm === 'SPOJKOVISTE';

  return {
    showSpecHp,
    showSpecHpVrBranch,
    showSpecHpBabovka,
    showSpecZafuk,
    showSpecRozvadecPrimaryZafuk,
    showSpecKoncovyBodZafuk,
    showSpecSpojkoviste,
  };
}
